import {ChangeDetectionStrategy, Component, InputSignal, WritableSignal, input, signal} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {FormButtonComponent} from '../../../../shared/elements/form-button/form-button.component';
import {FormLabelComponent} from '../../../../shared/elements/form-label/form-label.component';
import {TranslatePipe} from '../../../../shared/pipes/translate.pipe';
import {FeedbackStructureQuestionFormQuestion, FeedbackStructureQuestionOption} from '../../feedback-structure-create-update-dialog/feedback-structure-question/feedback-structure-question-form.model';
import {feedbackOptionControlName, previewOptionLabel} from '../../helpers/feedback-structure-preview-control-names';

@Component({
	selector: 'app-feedback-form-element-ordering',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormButtonComponent, FormLabelComponent, TranslatePipe],
	styles: [`
		.preview-ordering-list {
			margin: .5rem 0 .25rem;
		}

		.preview-ordering-row {
			background: var(--bs-card-bg);
			border: 1px solid var(--our-border-color);
			border-radius: var(--our-border-radius);
			box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
			min-height: 3.5rem;
			overflow: hidden;
		}

		.preview-ordering-index {
			border-right: 1px solid var(--our-border-color);
			color: var(--our-title-color);
			font-size: var(--our-font-size-default);
			font-weight: 700;
			min-width: 3rem;
			padding: .75rem;
		}

		.preview-ordering-label {
			color: var(--our-text-color);
			padding: .75rem;
		}

		.preview-ordering-label ::ng-deep label {
			color: var(--our-text-muted);
			font-size: var(--our-font-size-default);
			margin-bottom: 0;
		}

		.preview-ordering-actions {
			border-left: 1px solid var(--our-border-color);
			min-width: 3.5rem;
			padding: .5rem;
		}

		.preview-ordering-action {
			width: 2rem;
		}

		.preview-ordering-action-placeholder {
			display: block;
			height: 2rem;
			visibility: hidden;
			width: 2rem;
		}

		.preview-ordering-row-moving {
			animation: preview-ordering-move 250ms ease-out;
		}

		@media (max-width: 576px) {
			.preview-ordering-index {
				min-width: 2.75rem;
				padding: .75rem .5rem;
			}

			.preview-ordering-label {
				padding: .75rem .5rem;
			}
		}

		@keyframes preview-ordering-move {
			0% {
				border-color: var(--our-button-border-color);
				transform: scale(.99);
			}

			60% {
				border-color: var(--our-button-border-color);
				transform: scale(1.01);
			}
			
			100% {
				border-color: var(--our-button-border-color);
				transform: scale(1);
			}
		}
	`],
	template: `
		<div class="preview-ordering-list d-flex flex-column">
			@for (option of orderedOptions(); track option.position; let index = $index) {
				<div
					class="preview-ordering-row d-flex align-items-stretch mb-2"
					[class.preview-ordering-row-moving]="isMoving(option)"
					(animationend)="clearMovingOption(option)"
				>
					<div class="preview-ordering-index d-flex align-items-center justify-content-center">
						{{ index + 1 }}.
					</div>
					<div class="preview-ordering-label flex-grow-1 d-flex align-items-center w-100">
						<app-form-label [label]="getLabel(option, ('FEEDBACKANSWER' | translate))"></app-form-label>
					</div>
					<div class="preview-ordering-actions d-flex align-items-center justify-content-center gap-1">
						<div class="preview-ordering-action d-flex justify-content-center">
							@if (canMove(option, 'up')) {
								<app-form-button
									(click)="moveOption(option, 'up')"
									[cssClass]="'e-btn e-flat p-0 text-muted'"
									[icon]="'keyboard_arrow_up'"
									[iconClass]="'fs-5 m-0'"
									[ariaLabel]="'Move answer up'"
									[disabled]="disabled()"
								></app-form-button>
							} @else {
								<span class="preview-ordering-action-placeholder" aria-hidden="true"></span>
							}
						</div>
						<div class="preview-ordering-action d-flex justify-content-center">
							@if (canMove(option, 'down')) {
								<app-form-button
									(click)="moveOption(option, 'down')"
									[cssClass]="'e-btn e-flat p-0 text-muted'"
									[icon]="'keyboard_arrow_down'"
									[iconClass]="'fs-5 m-0'"
									[ariaLabel]="'Move answer down'"
									[disabled]="disabled()"
								></app-form-button>
							} @else {
								<span class="preview-ordering-action-placeholder" aria-hidden="true"></span>
							}
						</div>
					</div>
				</div>
			}
		</div>
	`,
})
export class FeedbackFormElementOrderingComponent {
	public question: InputSignal<FeedbackStructureQuestionFormQuestion> = input.required();
	public formGroup: InputSignal<FormGroup> = input.required();
	public controlNamePrefix: InputSignal<string> = input('preview');
	public disabled: InputSignal<boolean> = input(false);
	public movingOptionPositions: WritableSignal<Set<number>> = signal(new Set<number>());

	public getLabel(option: FeedbackStructureQuestionOption, fallback: string): string{
		return previewOptionLabel(option, fallback)
	};

	public optionControlName(option: FeedbackStructureQuestionOption): string {
		return feedbackOptionControlName(this.question(), option, this.controlNamePrefix());
	}

	public orderedOptions(): FeedbackStructureQuestionOption[] {
		return [...this.question().options].sort((first, second) => {
			return this.optionOrder(first) - this.optionOrder(second) || first.position - second.position;
		});
	}

	public canMove(option: FeedbackStructureQuestionOption, direction: 'up' | 'down'): boolean {
		if (this.disabled()) {
			return false;
		}

		const index = this.optionIndex(option);
		return direction === 'up'
			? index > 0
			: index >= 0 && index < this.question().options.length - 1;
	}

	public moveOption(option: FeedbackStructureQuestionOption, direction: 'up' | 'down'): void {
		if (this.disabled()) {
			return;
		}

		const options = this.orderedOptions();
		const index = options.findIndex((orderedOption) => orderedOption.position === option.position);
		const targetIndex = direction === 'up' ? index - 1 : index + 1;
		const targetOption = options[targetIndex];

		if (index < 0 || !targetOption) {
			return;
		}

		const control = this.formGroup().get(this.optionControlName(option));
		const targetControl = this.formGroup().get(this.optionControlName(targetOption));

		if (!control || !targetControl) {
			return;
		}

		const order = this.optionOrder(option);
		control.setValue(this.optionOrder(targetOption));
		targetControl.setValue(order);
		this.movingOptionPositions.set(new Set([option.position, targetOption.position]));
		control.markAsDirty();
		targetControl.markAsDirty();
		control.markAsTouched();
		targetControl.markAsTouched();
	}

	public isMoving(option: FeedbackStructureQuestionOption): boolean {
		return this.movingOptionPositions().has(option.position);
	}

	public clearMovingOption(option: FeedbackStructureQuestionOption): void {
		const movingOptionPositions = new Set(this.movingOptionPositions());
		movingOptionPositions.delete(option.position);
		this.movingOptionPositions.set(movingOptionPositions);
	}

	private optionIndex(option: FeedbackStructureQuestionOption): number {
		return this.orderedOptions().findIndex((orderedOption) => orderedOption.position === option.position);
	}

	private optionOrder(option: FeedbackStructureQuestionOption): number {
		const value = this.formGroup().get(this.optionControlName(option))?.value;
		const numericValue = typeof value === 'number' ? value : Number(value);

		return Number.isFinite(numericValue) ? numericValue : option.position + 1;
	}
}
